import React, { useState } from 'react'
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Paper
} from '@mui/material'
import { Icon as IconifyIcon } from "@iconify/react"
import VariantDetailsModal from './VariantDetailsModal'
import './ConflictingVariantsTable.scss'

export default function ConflictingVariantsTable({ data }) {
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(50)
    const [selectedVariant, setSelectedVariant] = useState(null)
    const [modalOpen, setModalOpen] = useState(false)

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const handleVariantClick = (variantId) => {
        setSelectedVariant(variantId)
        setModalOpen(true)
    }

    const handleModalClose = () => {
        setModalOpen(false)
        setSelectedVariant(null)
    }

    if (!data || data.length === 0) {
        return (
            <Card className="conflicting-variants-table-card">
                <CardContent>
                    <Typography variant="h6" className="table-title">
                        <IconifyIcon icon="mdi:alert-circle" className="title-icon" />
                        Konflikte
                    </Typography>
                    <Typography variant="body2" color="textSecondary" className="no-data-message">
                        Keine Konflikte  gefunden - ausgezeichnet!
                    </Typography>
                </CardContent>
            </Card>
        )
    }

    // Helper to determine if classifications are truly conflicting (P/LP vs B/LB)
    const isConflicting = (classifications) => {
        const hasPathogenic = classifications.some(c =>
            c.classification.toLowerCase().includes('pathogen')
        )
        const hasBenign = classifications.some(c =>
            c.classification.toLowerCase().includes('benign')
        )
        return hasPathogenic && hasBenign
    }

    // Paginate data
    const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

    return (
        <Card className="conflicting-variants-table-card">
            <CardContent>
                <Typography variant="h6" className="table-title">
                    <IconifyIcon icon="mdi:alert-circle" className="title-icon" />
                    Varianten ohne Konsensus
                </Typography>
                <Typography variant="caption" color="textSecondary" className="table-subtitle">
                    {data.length} Varianten-Erkrankung-Kontexte mit unterschiedlichen Bewertungen
                </Typography>

                <TableContainer component={Paper} className="table-container">
                    <Table size="small" className="variants-table">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Gen</strong></TableCell>
                                <TableCell><strong>Varianten-ID</strong></TableCell>
                                <TableCell><strong>Klassifikationen</strong></TableCell>
                                <TableCell align="center"><strong>Status</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedData.map((variant, index) => (
                                <TableRow
                                    key={`${variant.variantId}:${variant.contextKey || index}`}
                                    className="variant-row clickable"
                                    onClick={() => handleVariantClick(variant.variantId)}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                >
                                    <TableCell className="gene-cell">
                                        <strong>{variant.gene}</strong>
                                    </TableCell>
                                    <TableCell className="variant-id-cell">
                                        <code>{variant.variantId}</code><br /><small>{variant.condition}</small>
                                    </TableCell>
                                    <TableCell className="classifications-cell">
                                        <div className="classifications-list">
                                            {variant.classifications.map((cls, idx) => (
                                                <div key={idx} className="classification-item">
                                                    <span className="lab-name">{cls.lab}:</span>
                                                    <Chip
                                                        label={cls.classification}
                                                        size="small"
                                                        className={`classification-chip ${
                                                          cls.classification.toLowerCase().includes('pathogen')
                                                            ? 'pathogenic'
                                                            : cls.classification.toLowerCase().includes('unclear') || cls.classification.toLowerCase().includes('uncertain')
                                                              ? 'unclear'
                                                              : 'benign'
                                                        }`}                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </TableCell>
                                    <TableCell align="center" className="status-cell">
                                        <Chip
                                            label={variant.withinLabDisagreement ? 'Abweichende Laborbewertungen' : variant.type === 'conflict' ? 'Konflikt (P↔B)' : 'Kein Konsensus'}
                                            size="small"
                                            color={variant.type === 'conflict' ? 'error' : 'warning'}
                                            icon={<IconifyIcon icon={variant.type === 'conflict' ? 'mdi:alert' : 'mdi:help-circle'} />}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={data.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[25, 50, 100, 200]}
                    labelRowsPerPage="Varianten pro Seite:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
                />

                <VariantDetailsModal
                    open={modalOpen}
                    onClose={handleModalClose}
                    variantId={selectedVariant}
                />
            </CardContent>
        </Card>
    )
}
